import axios, { AxiosInstance } from 'axios'
import axiosRetry from 'axios-retry'
import getDistance from 'geolib/es/getDistance'
import { PCP_SERVICE_LINE_NAMES } from '_config/constants'
import { clearStorage } from '_helpers/storage'
import { Referral } from '_types/Referral'
import { convertDistance } from 'geolib'
import { API_INTERFACE, urlConstants } from './constants'
import { getIsPcpValue } from './helpers'
import {
  Patient,
  ReferralProvider,
  Member,
  PlanDetail,
  GetProvidersParams,
  ProviderDetails,
  SubmitReferralRequestData,
  GetProviderDetailParams,
  ServiceLine,
  ServiceLineOptions,
  GetProvidersSearch,
  GetProvidersFilter,
  GetPractitionersParams,
  Facility,
  Coords,
  OpensearchProvider,
} from './types'

class Client {
  private providerDomainInstance: AxiosInstance
  private providerPortalInstance: AxiosInstance
  private providerDomainApi: AxiosInstance

  routes: typeof API_INTERFACE

  networks: string[]

  constructor(authToken?: string | null) {
    this.providerDomainApi = axios.create({
      baseURL: process.env.CAREPOINT_URL || process.env.REACT_APP_CAREPOINT_URL,
    })
    this.providerDomainInstance = axios.create({
      timeout: 300000,
      baseURL: urlConstants.PROVIDER_API,
      headers: {
        'Content-Type': 'application/json;charset=UTH-8',
        Accept: 'application/json',
      },
    })
    this.providerPortalInstance = axios.create({
      timeout: 300000,
      baseURL: urlConstants.BASE_URL,
      headers: {
        'Content-Type': 'application/json;charset=UTH-8',
        Accept: 'application/json',
      },
    })

    axiosRetry(this.providerDomainInstance, {
      retries: 5,
      retryCondition: error =>
        error.message === 'Network Error' || error.response?.status === 504,
    })

    if (authToken) {
      this.setAuthToken(authToken)
    }
    this.networks = []
    this.routes = API_INTERFACE
    this.onUnauthorized = this.onUnauthorized.bind(this)

    this.providerDomainInstance.interceptors.response.use(
      res => res,
      error => {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          this.onUnauthorized()
        }

        return Promise.reject(error)
      }
    )
    this.providerPortalInstance.interceptors.response.use(
      res => res,
      error => {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          this.onUnauthorized()
        }

        return Promise.reject(error)
      }
    )
  }

  onUnauthorized() {
    delete this.providerDomainInstance.defaults.headers.common.Authorization
    clearStorage()
  }

  setAuthToken(authToken?: string) {
    if (!authToken) {
      delete this.providerDomainInstance.defaults.headers.common.Authorization
      delete this.providerPortalInstance.defaults.headers.common.Authorization

      return
    }

    this.providerDomainInstance.defaults.headers.common.Authorization = `Bearer ${authToken}`
    this.providerPortalInstance.defaults.headers.common.Authorization = `Bearer ${authToken}`
  }

  async login(data: { username: string; password: string }) {
    return this.providerPortalInstance.post<{
      authToken: string
    }>(this.routes.SIGN_IN_USER, data)
  }

  async getUser() {
    return this.providerPortalInstance.get(this.routes.GET_USER)
  }

  async getManagementReferralList(data: {
    tins: string[]
    startRange?: number
    chunkSize?: number
  }): Promise<Referral[]> {
    const { startRange = 0, chunkSize = 1024 * 1024 } = data

    const response = await this.providerPortalInstance.get<Referral[]>(
      this.routes.GET_MANAGEMENT_REFERRAL_LIST,
      {
        params: {
          tins: data.tins.join(),
          startRange,
          chunkSize,
          paginate: true,
        },
      }
    )

    return response.data
  }

  async getPatientsByMemberId(memberId: string) {
    return this.providerPortalInstance.get<Patient[]>(this.routes.FIND_MEMBER, {
      params: {
        memberId,
      },
    })
  }

  async getSpecialties(params?: { memberUuid?: string; isProvider?: boolean }) {
    return this.providerPortalInstance.get<ReferralProvider>(
      this.routes.GET_SPECIALTY_LIST,
      {
        params: {
          ...params,
          isProvider: true,
        },
      }
    )
  }

  async getPlanDetail(memberUuid: string) {
    return this.providerPortalInstance.get<PlanDetail>(
      this.routes.GET_PLAN_DETAIL,
      {
        params: {
          memberUuid,
        },
      }
    )
  }

  async getMemberDetail(memberUuid: string) {
    return this.providerPortalInstance.get<Member[]>(
      this.routes.GET_MEMBER_DETAIL,
      {
        params: {
          memberUuid,
        },
      }
    )
  }

  async getGMapKey() {
    return this.providerPortalInstance.get<{ key: string }>(
      this.routes.GET_GMAP_KEY
    )
  }

  async getProviders(params: GetProvidersParams) {
    return this.providerPortalInstance.get<ProviderDetails[]>(
      this.routes.FIND_PROVIDERS,
      {
        params,
      }
    )
  }

  async submitReferral(data: SubmitReferralRequestData) {
    return this.providerPortalInstance.post(
      this.routes.SUBMIT_REFERRAL_PROVIDER,
      data
    )
  }

  async getProviderDetail(params: GetProviderDetailParams) {
    return this.providerDomainApi.get('/provider/fetch', {
      params,
    })
  }

  async getServiceLines() {
    const { data } = await this.providerDomainInstance.get<ServiceLine[]>(
      this.routes.GET_SERVICE_LINES
    )

    const format = (serviceLine: ServiceLine): ServiceLineOptions => ({
      label: serviceLine.name,
      codes: serviceLine.codes.join(','),
      isPcp: serviceLine.isPcp,
    })

    return {
      doctor: data
        .filter(sl => sl.isProviderDoctor)
        .sort((x, y) => {
          if (PCP_SERVICE_LINE_NAMES.includes(x.name)) {
            return -1
          }
          if (PCP_SERVICE_LINE_NAMES.includes(y.name)) {
            return 1
          }

          return x.name.localeCompare(y.name)
        })
        .map(format),
      facility: data.filter(sl => sl.isProviderFacility).map(format),
    }
  }

  async paginate(url: string, params: any, pageSize = 1000) {
    let completeResponse: Record<string, any> | undefined = undefined
    let page = 0

    let response
    do {
      response = await this.providerDomainApi.get(url, {
        params: {
          ...params,
          pageSize,
          page,
        },
      })
      if (!completeResponse) completeResponse = response.data
      else completeResponse.data.push(response.data.data)
      page++
    } while (response?.data?.length === pageSize)

    return completeResponse
  }

  async getPracticesByZipCode<Params extends GetProvidersSearch>(
    search: Params,
    filter: GetProvidersFilter
  ) {
    this.networks = search.networks
    const params = {
      zipCode: search.zipCode,
      networks: search.networks.join(','),
      gender: filter.gender,
      isAcceptingNewPatients: filter.acceptingNewPatients,
      serviceLines: search?.serviceLines,
      distance: filter?.distance,
      name: search?.name,
    }


    const searchParams = filter.withVirtualCare 
    ? 
      {
        ...params,
        virtualOptions: 'y,only',
      } 
    : params
    
    const response = await this.paginate('/practice/zipCode', searchParams)
    return response?.data ?? []
  }

  async getProvidersByZipCode<Params extends GetProvidersSearch>(
    search: Params,
    filter: GetProvidersFilter
  ) {
    this.networks = search.networks
    const params = {
      zipCode: search.zipCode,
      networks: search.networks.join(','),
      gender: filter.gender,
      isAcceptingNewPatients: filter.acceptingNewPatients,
      serviceLines: search?.serviceLines,
      distance: filter?.distance,
      name: search?.name,
    }

    const searchParams = filter.withVirtualCare 
    ? 
      {
        ...params,
        virtualOptions: 'y,only',
      } 
    : params

    const response = await this.paginate('/provider/zipCode', searchParams)
    return response?.data ?? []
  }

  async getFacilities(search: GetProvidersSearch, filter: GetProvidersFilter) {
    const { zipCode, serviceLines, name, networks } = search
    const { specialtyFilters = [] } = filter
    this.networks = search.networks
    const params = {
      name: name || undefined,
      zipCode,
      distance: filter?.distance,
      serviceLines,
      networks: networks.join(','),
      specialties:
        specialtyFilters.length > 0 ? specialtyFilters.join(',') : undefined,
    }

    const response = await this.paginate(
      '/facility/opensearch/zipCode',
      params,
      100
    )
    return response?.data ?? []
  }

  async getFacilitiesByTin(tin: string, key?: string) {
    const params = {
      tin,
      key,
    }
    const response = await this.providerDomainApi.get('/facility/fetch', {
      params,
    })
    const facilities = response?.data?.data as Facility[]
    return facilities
  }

  async getPractitionersByServiceLines({
    practiceId,
    pos,
    search,
    isVirtualCareOnly,
    networks = this.networks,
    filter = {},
  }: GetPractitionersParams) {
    const { specialtyFilters = [] } = filter
    const params = {
      networks: networks?.join(','),
      lat: pos.lat,
      long: pos.long,
      practiceName: search.name,
      practiceId: practiceId,
      isPcp: getIsPcpValue(search.serviceLines),
      serviceLines: search.serviceLines,
      gender:
        filter && filter.gender === 'anyGender'
          ? undefined
          : filter && filter.gender,
      virtualOptions: isVirtualCareOnly ? 'only' : undefined,
      isAcceptingNewPatients:
        (filter && filter.acceptingNewPatients) || undefined,
      specialties:
        filter && specialtyFilters.length > 0
          ? specialtyFilters.join(',')
          : undefined,
    }

    const { data: response } = await this.providerDomainApi.get(
      '/provider/fetch',
      {
        params,
      }
    )

    return response.data ?? []
  }

  async getProvidersFetch(
    params: Record<string, any>
  ): Promise<OpensearchProvider[]> {
    const response = await this.providerDomainApi.get('/provider/fetch', {
      params,
    })
    return response?.data?.data ?? []
  }

  async getProvidersByPracticeId(
    params: Record<string, any>
  ): Promise<OpensearchProvider[]> {
    const response = await this.providerDomainApi.get('/provider/practiceId', {
      params: { pageSize: 1000, ...params },
    })
    return response?.data?.data ?? []
  }

  async getDistance(zipCode: string, coords: Coords) {
    const params = {
      zipCode,
    }
    const { data: coordsData } = await this.providerDomainApi.get(
      'getZipCodeLatAndLong',
      { params }
    )

    return convertDistance(getDistance(coordsData, coords), 'mi').toFixed(2)
  }

  async getValidateReferralExtension(data: {
    memberUuid: string
    referralUuid: string
  }) {
    try {
      return await this.providerPortalInstance.post(
        this.routes.VALIDATE_REFERRAL_EXTENSION,
        data
      )
    } catch (error: any) {
      return error.response.data
    }
  }
}

export const client = new Client()

export * from './types'

export * from './helpers'
